import { useCallback, useEffect, useMemo, useState, type CSSProperties, type SyntheticEvent } from 'react'
import { Sparkles } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  MetricComparisonKind,
  MethodEquity,
  ServerApiClient,
  ValuationAiNormalization,
  ValuationBaseInputs,
  ValuationBridge,
  ValuationFrameworkResult,
  ValuationHistory,
  ValuationHistoryPoint,
  ValuationSimulationRequest,
  ValuationSimulationResponse,
  ValuationOverviewResponse,
  ValueDriver,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { CfoSurfaceBeliefs } from './CfoSurfaceBeliefs'
import { FootballField, TrendLine, WaterfallBridge, type FootballMethod } from './charts'
import { CfoAiRead, CfoCardSkeletons, CfoChartSkeleton } from './CfoPagePrimitives'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { formatAsOf, formatCurrencyCompact, formatSignedCurrency, trimStr } from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import { cfoProFeaturesAvailable, useCfoBootstrap } from './useCfoBootstrap'
import { CfoProUpgradeNotice } from './CfoProUpgradeNotice'
import type { CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import { PinToReportButton, type AddToReportPayload } from './presentation/useAddToReport'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard } from './analysis/types'

const SURFACE = CFO_SURFACES.valuation

const MARKET_METHODS = new Set(['ebitda_multiple', 'revenue_multiple', 'earnings_multiple'])
const VALUATION_SOURCE_TABLES = [
  'qbo_fpa_cfo_valuation_inputs',
  'fg_cfo_ai_valuation_history',
  'fg_cfo_ai_statement_monthly_activity',
]

/** Method ids that participate in the adjustable blend, with display labels. */
const VALUATION_BLEND_METHODS: Array<{ id: string; label: string }> = [
  { id: 'earnings_multiple', label: 'Owner earnings' },
  { id: 'ebitda_multiple', label: 'EBITDA' },
  { id: 'revenue_multiple', label: 'Revenue' },
  { id: 'dcf', label: 'DCF' },
]

/** Short clause explaining why a blend method has no value under the current
 * inputs/scenario. Kept generic so it reads correctly at base and in a scenario. */
function methodUnavailableReason(id: string): string {
  switch (id) {
    case 'ebitda_multiple':
      return 'EBITDA is not positive'
    case 'earnings_multiple':
      return 'owner earnings are not positive'
    case 'revenue_multiple':
      return 'there is no revenue to multiply'
    case 'dcf':
      return 'projected cash flow or discount inputs are out of range'
    default:
      return 'the inputs are out of range'
  }
}

/**
 * Build the football-field methods for the range chart. Every blend method is
 * included in a stable order — even ones with no value under the current inputs
 * (e.g. an EBITDA multiple while EBITDA is negative) — flagged `applicable: false`
 * with a reason. Keeping unused methods visible lets a slider (like EBITDA margin)
 * bring one back into the estimate instead of the method silently disappearing.
 * Exported for tests.
 */
export function buildValuationFootballMethods(
  valuation: ValuationOverviewResponse | null,
): FootballMethod[] {
  if (!valuation) return []
  const frameworkById = new Map((valuation.frameworks || []).map((f) => [f.id, f]))
  const list: FootballMethod[] = VALUATION_BLEND_METHODS.map(({ id, label }) => {
    const framework = frameworkById.get(id)
    const rawMid = framework?.equity_value_mid ?? framework?.equity_value ?? null
    const mid = rawMid != null && Number.isFinite(rawMid) ? rawMid : null
    return {
      id,
      label: framework?.label || label,
      low: mid != null ? framework?.equity_value_low ?? mid : 0,
      mid: mid ?? 0,
      high: mid != null ? framework?.equity_value_high ?? mid : 0,
      market: MARKET_METHODS.has(id),
      applicable: mid != null,
      unusedReason: mid != null ? undefined : `Not used — ${methodUnavailableReason(id)}`,
    }
  })
  if (valuation.equity_value_low != null && valuation.equity_value_high != null) {
    list.push({
      id: 'blended',
      label: 'Blended estimate',
      low: valuation.equity_value_low,
      mid: valuation.equity_value_mid ?? valuation.equity_value_low,
      high: valuation.equity_value_high,
      applicable: true,
    })
  }
  return list
}

const valuationFiniteNumber = (value: unknown): number | null => {
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

const valuationPctChangeText = (current: number | null, prior: number | null): string => {
  if (current == null || prior == null || Math.abs(prior) < 0.005) return '-'
  const pct = (current - prior) / Math.abs(prior)
  const sign = pct > 0 ? '+' : pct < 0 ? '-' : ''
  return `${sign}${Math.abs(pct * 100).toFixed(1)}%`
}

const valuationComparisonTone = (current: number | null, prior: number | null) => {
  if (current == null || prior == null) return 'neutral'
  const delta = current - prior
  if (Math.abs(delta) < 0.005) return 'neutral'
  return delta > 0 ? 'good' : 'bad'
}

const valuationWindowAverage = (points: ValuationHistoryPoint[], indices: number[]): number | null => {
  let total = 0
  let count = 0
  for (const index of indices) {
    const value = valuationFiniteNumber(points[index]?.equity_value_mid)
    if (value == null) continue
    total += value
    count += 1
  }
  return count ? total / count : null
}

const valuationComparisonRows = (
  valuation: ValuationOverviewResponse,
  selectedPoint: ValuationHistoryPoint | null,
) => {
  const points = valuation.history?.points || []
  const selectedMonth = periodMonthKey(selectedPoint?.period || valuation.as_of)
  const currentIndex = selectedMonth
    ? Math.max(0, points.findIndex((point) => periodMonthKey(point.period) === selectedMonth))
    : Math.max(0, points.length - 1)
  const current = valuationFiniteNumber(selectedPoint?.equity_value_mid ?? valuation.equity_value_mid)
  const currentPeriod = trimStr(points[currentIndex]?.period || valuation.as_of)
  const currentYear = Number(currentPeriod.slice(0, 4))
  const currentMonth = Number(currentPeriod.slice(5, 7))
  const ytdIndices = points
    .map((point, index) => ({ period: trimStr(point.period), index }))
    .filter(({ period, index }) => Number(period.slice(0, 4)) === currentYear && Number(period.slice(5, 7)) <= currentMonth && index <= currentIndex)
    .map(({ index }) => index)
  const priorYtdIndices = points
    .map((point, index) => ({ period: trimStr(point.period), index }))
    .filter(({ period }) => Number(period.slice(0, 4)) === currentYear - 1 && Number(period.slice(5, 7)) <= currentMonth)
    .map(({ index }) => index)
  const row = (label: string, currentValue: number | null, priorValue: number | null) => ({
    label,
    current: currentValue == null ? '-' : formatCurrencyCompact(currentValue),
    prior: priorValue == null ? '-' : formatCurrencyCompact(priorValue),
    change: valuationPctChangeText(currentValue, priorValue),
    tone: valuationComparisonTone(currentValue, priorValue),
    unavailableReason: currentValue == null || priorValue == null ? 'Not available in the valuation history window.' : null,
  })
  return [
    row('Current month', current, valuationFiniteNumber(points[currentIndex - 1]?.equity_value_mid)),
    row('Last 3 months', valuationWindowAverage(points, [currentIndex - 2, currentIndex - 1, currentIndex]), valuationWindowAverage(points, [currentIndex - 5, currentIndex - 4, currentIndex - 3])),
    row('YTD', valuationWindowAverage(points, ytdIndices), valuationWindowAverage(points, priorYtdIndices)),
    row('Prior year', current, valuationFiniteNumber(points[currentIndex - 12]?.equity_value_mid)),
  ]
}

const valuationTrend = (valuation: ValuationOverviewResponse) => {
  const points = (valuation.history?.points || []).slice(-12).map((point) => {
    const value = valuationFiniteNumber(point.equity_value_mid)
    return {
      label: formatAsOf(point.period).replace(/\s+\d{4}$/, ''),
      period: trimStr(point.period) || null,
      rawValue: value,
      value: value == null ? '-' : formatCurrencyCompact(value),
    }
  })
  return {
    label: points.length >= 12 ? '12-month trend' : 'Available valuation trend',
    points,
    unavailable_reason: points.length >= 12 ? null : `Valuation artifact has ${points.length} monthly valuation points.`,
  }
}

const valuationSourceFingerprint = (valuation: ValuationOverviewResponse | null): string =>
  [
    SURFACE,
    trimStr(valuation?.signature || ''),
    trimStr(valuation?.valuation_set_version || ''),
    trimStr(valuation?.as_of || ''),
    valuation?.history?.points?.length || 0,
    valuation?.history?.points?.slice(-3).map((point) => `${periodMonthKey(point.period)}:${valuationFiniteNumber(point.equity_value_mid) ?? 0}`).join('|') || '',
  ].join(':')

export const buildValuationInstantCards = (args: {
  valuation: ValuationOverviewResponse | null
  asOfLabel: string
  comparisonKind: MetricComparisonKind
}): CfoInstantCard[] => {
  const { valuation, asOfLabel, comparisonKind } = args
  if (!valuation) return []
  const sourceFingerprint = valuationSourceFingerprint(valuation)
  const selectedPoint = findSelectedValuationHistoryPoint(valuation.history, valuation.as_of)
  const trend = valuationTrend(valuation)
  const comparisons = valuationComparisonRows(valuation, selectedPoint)
  const baseContext = {
    workbookId: null,
    pageId: 'valuation',
    activeSection: SURFACE,
    period: valuation.as_of || null,
    comparison: comparisonKind,
    asOfLabel,
    sourceTables: VALUATION_SOURCE_TABLES,
    grounding: null,
    instantCard: null,
  }
  const cards: CfoInstantCard[] = []
  const addCard = (metricId: string, metricLabel: string, value: number | null, sourceComponent: string, selection: Record<string, any>) => {
    if (value == null) return
    cards.push(buildCfoInstantCardFromContext({
      context: {
        ...baseContext,
        metricId,
        metricLabel,
        sourceComponent,
        selection: {
          selection_key: `valuation:${metricId}`,
          value,
          source_fingerprint: sourceFingerprint,
          signature: valuation.signature,
          artifact_ids: ['valuation_overview'],
          instant_card_trend: trend,
          instant_card_comparisons: comparisons,
          instant_card_insights: selection.instant_card_insights,
          instant_card_prescriptive_actions: selection.instant_card_prescriptive_actions,
          instant_card_next_steps: selection.instant_card_next_steps,
          deterministic_read: selection.deterministic_read,
          ...selection,
        },
      },
      visual: null,
    }))
  }
  addCard('equity_value_mid', 'Valuation estimate', valuationFiniteNumber(valuation.equity_value_mid), 'valuation_estimate', {
    deterministic_read: `The blended valuation estimate is ${formatCurrencyCompact(valuationFiniteNumber(valuation.equity_value_mid))} as of ${asOfLabel}.`,
    instant_card_insights: ['Valuation changes should be read through revenue scale, margin quality, cash, debt, and method weighting.'],
    instant_card_prescriptive_actions: ['Inspect the method contribution and the operating drivers before changing valuation assumptions.'],
    instant_card_next_steps: [
      { kind: 'drilldown', label: 'Inspect method contribution', prompt: 'Show the valuation method contribution and weights.', target: { surface: SURFACE, metricId: 'equity_value_mid' } },
      { kind: 'compare', label: 'Compare valuation periods', prompt: 'Compare valuation estimate across the required periods.', target: { surface: SURFACE, metricId: 'equity_value_mid' } },
      { kind: 'open_surface', label: 'Open valuation drivers', prompt: 'Open valuation driver detail.', target: { surface: SURFACE, metricId: 'equity_value_mid' } },
    ],
  })
  addCard('revenue_ttm', 'Revenue (TTM)', valuationFiniteNumber(valuation.base_inputs?.revenue_ttm), 'valuation_driver', {
    deterministic_read: `Trailing-twelve-month revenue is ${formatCurrencyCompact(valuationFiniteNumber(valuation.base_inputs?.revenue_ttm))}, the base for revenue-multiple and growth-sensitive methods.`,
    instant_card_insights: ['Revenue quality matters most when buyers trust repeatability by service line, customer type, and collection behavior.'],
    instant_card_prescriptive_actions: ['Inspect revenue trend and customer/service concentration before raising growth assumptions.'],
    instant_card_next_steps: [
      { kind: 'drilldown', label: 'Inspect revenue drivers', prompt: 'Show revenue drivers behind valuation.', target: { surface: SURFACE, metricId: 'revenue_ttm' } },
      { kind: 'open_surface', label: 'Open Financials', prompt: 'Open revenue financial detail.', target: { surface: CFO_SURFACES.financials, metricId: 'revenue_ttm' } },
      { kind: 'compare', label: 'Compare periods', prompt: 'Compare revenue TTM across required periods.', target: { surface: SURFACE, metricId: 'revenue_ttm' } },
    ],
  })
  addCard('cash', 'Cash used in valuation', valuationFiniteNumber(valuation.base_inputs?.cash), 'valuation_driver', {
    deterministic_read: `Cash adds ${formatCurrencyCompact(valuationFiniteNumber(valuation.base_inputs?.cash))} to equity value after enterprise value is estimated.`,
    instant_card_insights: ['Cash changes valuation directly, but timing and one-time movements need separation from operating performance.'],
    instant_card_prescriptive_actions: ['Inspect cash timing and debt movement before treating valuation changes as operational improvement.'],
    instant_card_next_steps: [
      { kind: 'open_surface', label: 'Open Cash', prompt: 'Open cash timing detail.', target: { surface: CFO_SURFACES.cash, metricId: 'cash' } },
      { kind: 'compare', label: 'Compare cash periods', prompt: 'Compare valuation cash across required periods.', target: { surface: SURFACE, metricId: 'cash' } },
    ],
  })
  for (const driver of (valuation.drivers || []).slice(0, 4)) {
    const rawValue = valuationFiniteNumber((driver as any).value ?? (driver as any).current_value ?? (driver as any).impact)
    if (rawValue == null) continue
    addCard(`driver:${trimStr(driver.id) || trimStr(driver.label)}`, trimStr(driver.label) || 'Valuation driver', rawValue, 'valuation_driver', {
      deterministic_read: trimStr(driver.explanation) || `${driver.label} is one of the valuation drivers for ${asOfLabel}.`,
      instant_card_insights: [trimStr(driver.explanation) || `${driver.label} affects the valuation estimate.`],
      instant_card_prescriptive_actions: ['Translate this driver into one operating action before changing valuation assumptions.'],
      instant_card_next_steps: [
        { kind: 'drilldown', label: `Inspect ${driver.label}`, prompt: `Show valuation evidence for ${driver.label}.`, target: { surface: SURFACE, metricId: trimStr(driver.id) || trimStr(driver.label) } },
        { kind: 'compare', label: 'Compare periods', prompt: `Compare ${driver.label} across valuation periods.`, target: { surface: SURFACE, metricId: trimStr(driver.id) || trimStr(driver.label) } },
      ],
    })
  }
  return cards
}

export type CfoValuationPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export function CfoValuationPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoValuationPageProps) {
  const [valuation, setValuation] = useState<ValuationOverviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { bootstrap, cacheState, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  // Valuation is a Pro surface.
  const proFeatures = cfoProFeaturesAvailable(bootstrap)

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    if (!proFeatures) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    const loadValuation = (appApi.getCfoValuation || appApi.getQuickBooksValuation)?.bind(appApi)
    if (typeof loadValuation !== 'function') {
      setLoading(false)
      setError('The valuation reporting contract is not available in this client session.')
      return
    }
    loadValuation(wb, { grain: 'month' })
      .then((v) => {
        if (cancelled) return
        setValuation(v)
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(trimStr(err?.message || err) || 'Could not load the valuation.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, proFeatures, workbookId])

  const selectedValuation = useMemo(
    () => deriveValuationForSelectedPeriod(valuation, selectedEndPeriod),
    [selectedEndPeriod, valuation],
  )
  const valuationPeriodOptions = useMemo(
    () => valuationPeriodOptionsFromHistory(valuation, periodOptions),
    [periodOptions, valuation],
  )

  useEffect(() => {
    const latestHistoryPeriod = valuation?.history?.points?.at(-1)?.period
    const canonicalAsOf = trimStr(latestHistoryPeriod || valuation?.as_of || '')
    if (!selectedEndPeriod && canonicalAsOf) onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [onSelectedEndPeriodChange, selectedEndPeriod, valuation])

  const asOfLabel = useMemo(() => formatAsOf(selectedValuation?.as_of || selectedEndPeriod || null), [selectedEndPeriod, selectedValuation])
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { openGlDetail, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts,
    onNavSelect,
    period: selectedValuation?.as_of || selectedEndPeriod || null,
  })

  const methods = useMemo<FootballMethod[]>(
    () => buildValuationFootballMethods(selectedValuation),
    [selectedValuation],
  )

  const drivers = useMemo<ValueDriver[]>(() => (selectedValuation?.drivers || []), [selectedValuation])
  const analysisSelection = useMemo(() => valuationAnalysisContext(selectedValuation), [selectedValuation])
  const valuationInstantCards = useMemo(
    () => buildValuationInstantCards({ valuation: selectedValuation, asOfLabel, comparisonKind }),
    [asOfLabel, comparisonKind, selectedValuation],
  )
  const valuationPageFactsFingerprint = useMemo(
    () => valuationSourceFingerprint(selectedValuation),
    [selectedValuation],
  )

  const lowMid = selectedValuation?.equity_value_low
  const highMid = selectedValuation?.equity_value_high
  const blendedMid = selectedValuation?.equity_value_mid ?? null
  const personalizationSummary = selectedValuation
    ? valuationPersonalizationLabel(selectedValuation).replace(/^Personalized from\s+/i, '')
    : ''
  const heroHeadline = blendedMid != null
    ? `Valuation estimate: ${formatCurrencyCompact(blendedMid)}`
    : 'What is this business worth?'
  const heroSubtext = lowMid != null && highMid != null
    ? `${asOfLabel}: market-weighted range ${formatCurrencyCompact(lowMid)}-${formatCurrencyCompact(highMid)}${personalizationSummary ? ` using ${personalizationSummary}` : ''}. Method bands show the active blended midpoint of ${formatCurrencyCompact(blendedMid ?? (lowMid + highMid) / 2)}. Educational estimate, not an appraisal.`
    : 'An educational estimate, not an appraisal. Build the range from standard methods and see what moves the number.'

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="valuation"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Plan"
      title="Valuation"
      hidePageHead
      crumb={
        <>
          <b>Valuation</b> · Estimate
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={valuationPeriodOptions}
      periodValue={selectedValuation?.as_of || selectedEndPeriod || undefined}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select CFO Core end period"
      comparisonLabel={comparisonOptions.find((option) => option.id === comparisonKind)?.label || 'vs. prior month'}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={cacheState}
      pageLoading={loading || bootstrapLoading}
      pageLoadingLabel="Loading valuation context…"
      copilotSubtitle="reading this valuation"
      copilotPlaceholder="Ask about Valuation…"
      copilotIntro="You’re on Valuation. Ask me how a method works, what raises the number, or how a buyer would see this business."
      askPlaceholder="Ask about this valuation…"
      suggestedPrompts={['How does a DCF work?', 'What would raise my multiple?', 'Simulate 10% growth']}
      sourceTables={VALUATION_SOURCE_TABLES}
      analysisSelection={analysisSelection}
      instantCards={valuationInstantCards}
      pageFactsFingerprint={valuationPageFactsFingerprint}
      openGlDetail={openGlDetail}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {({ copilot }) => (
        !proFeatures ? (
          <CfoProUpgradeNotice appApi={appApi} workbookId={workbookId} title="Valuation is part of Pro">
            See what your business is worth — four methods, blended, with the levers that move the number ranked. It’s part of Pro.
          </CfoProUpgradeNotice>
        ) : (
        <>
          {glDetailModal}
          <CfoSurfaceBeliefs
            appApi={appApi}
            workbookId={workbookId}
            filter={(c) =>
              c.directiveKind === 'forecast.normalize_out' ||
              c.directiveKind === 'forecast.data_quality_caveat'
            }
            heading="What the AI flagged for your valuation"
          />
          <ValuationBody
            loading={loading}
            error={error}
            valuation={selectedValuation}
            methods={methods}
            drivers={drivers}
            asOfLabel={asOfLabel}
            blendedMid={blendedMid}
            heroHeadline={heroHeadline}
            heroSubtext={heroSubtext}
            explainDriver={(driver) => {
              void copilot.sendMessage(
                `Give a concise, prescriptive valuation read for this specific company focused on ${driver.label}. ${driver.explanation} Use the valuation assumptions, company context, industry context, market benchmark, and visible value-driver evidence in workspace context. Lead with what the company should do first to increase value, then 2-3 concrete actions. Keep it friendly and do not include generic caveats unless a material data limitation changes the recommendation.`,
                {
                  workspace_context: {
                    ...(valuationAnalysisContext(selectedValuation) || {}),
                    interaction: 'valuation_driver_explain',
                    source_component: 'valuation_driver_card',
                    source_tables: VALUATION_SOURCE_TABLES,
                    selected_driver_id: driver.id,
                    driver,
                  },
                },
              )
              document.querySelector<HTMLTextAreaElement>('.cfo-copilot-input-box')?.focus()
            }}
            explainHistory={(payload) => {
              void copilot.sendMessage(
                `Explain what is driving the change in this company's modeled value ${payload.bridgeLabel}. The modeled value moved ${payload.changeText}. Break down how revenue/growth, margin/profitability, and the cash & debt position each contributed, using the value-bridge steps and the modeled-value history in workspace context. Be specific to this company and prescriptive: what recent activity helped or hurt value, and what to do next. Keep it friendly and concrete.`,
                {
                  workspace_context: {
                    ...(valuationAnalysisContext(selectedValuation) || {}),
                    interaction: 'valuation_history_explain',
                    source_component: 'valuation_history_section',
                    source_tables: VALUATION_SOURCE_TABLES,
                    selected_bridge: payload.bridge,
                    blend_weights: payload.weights,
                    valuation_history: selectedValuation?.history || null,
                  },
                },
              )
              document.querySelector<HTMLTextAreaElement>('.cfo-copilot-input-box')?.focus()
            }}
          />
          {classicEscapes ? (
            <button type="button" className="btn ghost" style={{ marginTop: 'var(--s4)' }} onClick={onOpenClassic}>
              Open valuation workspace
            </button>
          ) : null}
        </>
        )
      )}
    </CfoInteriorPage>
  )
}

type ValuationBodyProps = {
  loading: boolean
  error: string
  valuation: ValuationOverviewResponse | null
  methods: FootballMethod[]
  drivers: ValueDriver[]
  asOfLabel: string
  blendedMid: number | null
  heroHeadline: string
  heroSubtext: string
  explainDriver: (driver: ValueDriver) => void
  explainHistory: (payload: {
    bridge: ValuationBridge
    bridgeLabel: string
    changeText: string
    weights: Record<string, number>
  }) => void
}

type ValuationScenario = {
  forecastGrowth: number
  ebitdaMargin: number | null
  wacc: number
  ebitdaMultiple: number
  earningsMultiple: number
  terminalGrowth: number
}

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))
const roundCurrency = (value: number) => Math.round(value * 100) / 100

function valuationPersonalizationLabel(valuation: ValuationOverviewResponse | null): string {
  const context = valuation?.personalization_context
  const industry = trimStr(context?.industry?.industry_title || '')
  const companySummary = trimStr(context?.company?.business_summary || context?.company?.ai_context_notes || '')
  const market = trimStr(context?.market_benchmark?.category_label || '')
  if (industry && market && companySummary) return `Personalized from ${industry} + ${market} comps + company context`
  if (industry && market) return `Personalized from ${industry} + ${market} comps`
  if (industry && companySummary) return `Personalized from ${industry} + company context`
  if (industry) return `Personalized from ${industry}`
  if (market && companySummary) return `Personalized from ${market} comps + company context`
  if (market) return `Personalized from ${market} comps`
  if (companySummary) return 'Personalized from company context'
  return 'Personalized from company actuals'
}

function valuationAiContext(valuation: ValuationOverviewResponse | null) {
  const context = valuation?.personalization_context
  return {
    valuation_industry_context: context?.industry || null,
    company_context: context?.company || null,
    valuation_market_benchmark: context?.market_benchmark || null,
    assumption_industry: context?.assumption_industry || valuation?.assumptions.industry || null,
  }
}

function valuationAnalysisContext(valuation: ValuationOverviewResponse | null) {
  if (!valuation) return null
  return {
    interaction: 'valuation_page',
    source_component: 'valuation_page',
    source_tables: VALUATION_SOURCE_TABLES,
    ...valuationAiContext(valuation),
    valuation_summary: {
      as_of: valuation.as_of,
      equity_value_low: valuation.equity_value_low,
      equity_value_mid: valuation.equity_value_mid,
      equity_value_high: valuation.equity_value_high,
      signature: valuation.signature,
      valuation_set_version: valuation.valuation_set_version,
    },
    valuation_base_inputs: valuation.base_inputs,
    valuation_assumptions: valuation.assumptions,
    valuation_drivers: valuation.drivers,
    valuation_methods: valuation.frameworks.map((framework) => ({
      id: framework.id,
      label: framework.label,
      method_summary: framework.method_summary,
      reconciliation_weight: framework.reconciliation_weight ?? null,
      reconciliation_note: framework.reconciliation_note || null,
      equity_value_low: framework.equity_value_low ?? null,
      equity_value_mid: framework.equity_value_mid ?? framework.equity_value ?? null,
      equity_value_high: framework.equity_value_high ?? null,
      equity_value: framework.equity_value,
      assumptions_text: framework.assumptions_text,
    })),
    valuation_history: valuation.history
      ? {
          points: valuation.history.points.map((point) => ({
            period: point.period,
            equity_value_mid: point.equity_value_mid,
            revenue_ttm: point.revenue_ttm,
            ebitda_ttm: point.ebitda_ttm,
            net_income_ttm: point.net_income_ttm,
          })),
          bridge_mom_total: valuation.history.bridge_mom?.total_change ?? null,
          bridge_yoy_total: valuation.history.bridge_yoy?.total_change ?? null,
          default_weights: valuation.history.default_weights,
        }
      : null,
    warnings: valuation.warnings,
  }
}

function periodMonthKey(period: string | null | undefined): string {
  const raw = trimStr(period)
  return /^\d{4}-\d{2}/.test(raw) ? raw.slice(0, 7) : raw
}

function findSelectedValuationHistoryPoint(
  history: ValuationHistory | null | undefined,
  selectedPeriod: string | null | undefined,
): ValuationHistoryPoint | null {
  const points = history?.points || []
  const selectedMonth = periodMonthKey(selectedPeriod)
  if (!points.length || !selectedMonth) return null
  return points.find((point) => periodMonthKey(point.period) === selectedMonth) || null
}

function valuationPeriodOptionsFromHistory(
  valuation: ValuationOverviewResponse | null,
  fallbackOptions: CfoShellSelectOption[],
): CfoShellSelectOption[] {
  const points = valuation?.history?.points || []
  if (!points.length) {
    const asOf = trimStr(valuation?.as_of || '')
    return asOf ? [{ id: asOf, label: formatAsOf(asOf) }] : fallbackOptions
  }
  const seen = new Set<string>()
  const out: CfoShellSelectOption[] = []
  for (const point of [...points].reverse()) {
    const month = periodMonthKey(point.period)
    if (!month || seen.has(month)) continue
    seen.add(month)
    out.push({ id: point.period, label: formatAsOf(point.period) })
  }
  return out.length ? out : fallbackOptions
}

function historyThroughSelectedPoint(
  history: ValuationHistory | null | undefined,
  selectedPoint: ValuationHistoryPoint,
): ValuationHistory | null | undefined {
  if (!history) return history
  const selectedMonth = periodMonthKey(selectedPoint.period)
  const points = history.points.filter((point) => periodMonthKey(point.period) <= selectedMonth)
  const trailingPoints = points.slice(-12)
  const bridgeEndsAtOrBeforeSelection = (bridge: ValuationBridge | null | undefined) =>
    bridge && periodMonthKey(bridge.to_period) <= selectedMonth ? bridge : null
  return {
    ...history,
    points: trailingPoints,
    bridge_mom: bridgeEndsAtOrBeforeSelection(history.bridge_mom),
    bridge_yoy: bridgeEndsAtOrBeforeSelection(history.bridge_yoy),
  }
}

function baseInputsFromHistoryPoint(
  base: ValuationBaseInputs,
  point: ValuationHistoryPoint,
): ValuationBaseInputs {
  return {
    ...base,
    revenue_ttm: point.revenue_ttm,
    ebitda_ttm: point.ebitda_ttm,
    net_income_ttm: point.net_income_ttm,
    total_debt: point.total_debt,
    cash: point.cash,
    revenue_growth: point.revenue_growth,
    net_margin: point.net_margin,
    ebitda_margin: point.ebitda_margin,
  }
}

function frameworkFromHistoryMethod(
  method: MethodEquity,
  template: ValuationFrameworkResult | undefined,
  baseInputs: ValuationBaseInputs,
  period: string,
): ValuationFrameworkResult {
  const equityMid = method.equity_value_mid
  const enterpriseRange = bridgeEquityBandToEnterprise({
    low: method.equity_value_low,
    mid: equityMid,
    high: method.equity_value_high,
  }, baseInputs)
  return {
    id: method.id,
    label: template?.label || method.id.replace(/_/g, ' '),
    method_summary: template?.method_summary || '',
    reconciliation_weight: template?.reconciliation_weight ?? null,
    reconciliation_note: template?.reconciliation_note || null,
    enterprise_value_low: enterpriseRange.low,
    enterprise_value_mid: enterpriseRange.mid,
    enterprise_value_high: enterpriseRange.high,
    enterprise_value: enterpriseRange.mid,
    equity_value_low: method.equity_value_low,
    equity_value_mid: equityMid,
    equity_value_high: method.equity_value_high,
    equity_value: equityMid,
    inputs: {
      ...(template?.inputs || {}),
      history_period: period,
      revenue_ttm: baseInputs.revenue_ttm,
      ebitda_ttm: baseInputs.ebitda_ttm,
      net_income_ttm: baseInputs.net_income_ttm,
      total_debt: baseInputs.total_debt,
      cash: baseInputs.cash,
    },
    assumptions_text: template?.assumptions_text || [],
  }
}

export function deriveValuationForSelectedPeriod(
  valuation: ValuationOverviewResponse | null,
  selectedPeriod: string | null | undefined,
): ValuationOverviewResponse | null {
  if (!valuation) return null
  const selectedPoint = findSelectedValuationHistoryPoint(valuation.history, selectedPeriod)
  if (!selectedPoint || periodMonthKey(selectedPoint.period) === periodMonthKey(valuation.as_of)) {
    return valuation
  }
  const baseInputs = baseInputsFromHistoryPoint(valuation.base_inputs, selectedPoint)
  const frameworkById = new Map(valuation.frameworks.map((framework) => [framework.id, framework]))
  const frameworks = selectedPoint.methods.map((method) =>
    frameworkFromHistoryMethod(method, frameworkById.get(method.id), baseInputs, selectedPoint.period),
  )
  const blendedRange = selectedPoint.equity_value_low != null || selectedPoint.equity_value_mid != null || selectedPoint.equity_value_high != null
    ? {
        low: selectedPoint.equity_value_low,
        mid: selectedPoint.equity_value_mid,
        high: selectedPoint.equity_value_high,
      }
    : equityValueRange(frameworks)
  return {
    ...valuation,
    as_of: selectedPoint.period,
    signature: `${valuation.signature}:history:${periodMonthKey(selectedPoint.period)}`,
    base_inputs: baseInputs,
    frameworks,
    equity_value_low: blendedRange.low,
    equity_value_mid: blendedRange.mid,
    equity_value_high: blendedRange.high,
    history: historyThroughSelectedPoint(valuation.history, selectedPoint),
  }
}

function buildBaseScenario(valuation: ValuationOverviewResponse | null): ValuationScenario | null {
  if (!valuation) return null
  return {
    forecastGrowth: valuation.assumptions.forecast_growth,
    ebitdaMargin: isFiniteNumber(valuation.base_inputs.ebitda_margin) ? valuation.base_inputs.ebitda_margin : null,
    wacc: valuation.assumptions.wacc,
    ebitdaMultiple: valuation.assumptions.ebitda_multiple,
    earningsMultiple: valuation.assumptions.earnings_multiple,
    terminalGrowth: valuation.assumptions.terminal_growth,
  }
}

function scenarioDiff(base: ValuationScenario | null, next: ValuationScenario | null): ValuationSimulationRequest {
  if (!base || !next) return {}
  return {
    revenue_growth_delta_pct: next.forecastGrowth - base.forecastGrowth,
    ebitda_margin_delta_pts: next.ebitdaMargin != null && base.ebitdaMargin != null ? next.ebitdaMargin - base.ebitdaMargin : null,
    wacc_delta_pct: next.wacc - base.wacc,
    ebitda_multiple_delta: next.ebitdaMultiple - base.ebitdaMultiple,
    earnings_multiple_delta: next.earningsMultiple - base.earningsMultiple,
    terminal_growth_delta_pct: next.terminalGrowth - base.terminalGrowth,
  }
}

function hasScenarioChanges(req: ValuationSimulationRequest): boolean {
  return Object.values(req).some((value) => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) > 0.00001)
}

function formatPercentPoint(value: number | null | undefined, digits = 1): string {
  if (!isFiniteNumber(value)) return '—'
  return `${(value * 100).toFixed(digits)}%`
}

function formatSignedPercentPointDelta(value: number | null | undefined): string {
  if (!isFiniteNumber(value) || Math.abs(value) < 0.00001) return 'base'
  const sign = value > 0 ? '+' : '−'
  return `${sign}${Math.abs(value * 100).toFixed(1)} pts`
}

function formatSignedMultipleDelta(value: number | null | undefined): string {
  if (!isFiniteNumber(value) || Math.abs(value) < 0.00001) return 'base'
  const sign = value > 0 ? '+' : '−'
  return `${sign}${Math.abs(value).toFixed(1)}x`
}

function equityFromEnterprise(enterprise: number, base: ValuationBaseInputs): number {
  return enterprise - (base.total_debt ?? 0) + (base.cash ?? 0)
}

function enterpriseFromEquity(equity: number, base: ValuationBaseInputs): number {
  return equity + (base.total_debt ?? 0) - (base.cash ?? 0)
}

function rangeAroundMid(mid: number | null | undefined, lowFactor: number, highFactor: number) {
  if (!isFiniteNumber(mid)) return { low: null, mid: null, high: null }
  return {
    low: roundCurrency(mid * lowFactor),
    mid: roundCurrency(mid),
    high: roundCurrency(mid * highFactor),
  }
}

function computeDcf(baseCashFlow: number | null | undefined, growth: number, years: number, terminalGrowth: number, wacc: number): number | null {
  if (!isFiniteNumber(baseCashFlow) || baseCashFlow <= 0) return null
  if (wacc <= terminalGrowth + Number.EPSILON || wacc <= 0 || years <= 0) return null
  let pvSum = 0
  let cashFlow = baseCashFlow
  for (let year = 1; year <= years; year += 1) {
    cashFlow *= 1 + growth
    pvSum += cashFlow / ((1 + wacc) ** year)
  }
  const terminalCashFlow = cashFlow * (1 + terminalGrowth)
  const terminalValue = terminalCashFlow / (wacc - terminalGrowth)
  pvSum += terminalValue / ((1 + wacc) ** years)
  return Number.isFinite(pvSum) && pvSum > 0 ? pvSum : null
}

function computeDcfSchedule(annualCashFlows: number[], terminalGrowth: number, wacc: number): number | null {
  if (!annualCashFlows.length || !annualCashFlows.every((cf) => Number.isFinite(cf))) return null
  if (wacc <= terminalGrowth + Number.EPSILON || wacc <= 0) return null
  let pvSum = 0
  annualCashFlows.forEach((cf, idx) => {
    const year = idx + 1
    pvSum += cf / ((1 + wacc) ** year)
  })
  const last = annualCashFlows[annualCashFlows.length - 1]
  if (last > 0) {
    const years = annualCashFlows.length
    const terminalCashFlow = last * (1 + terminalGrowth)
    const terminalValue = terminalCashFlow / (wacc - terminalGrowth)
    pvSum += terminalValue / ((1 + wacc) ** years)
  }
  return Number.isFinite(pvSum) && pvSum > 0 ? pvSum : null
}

function dcfForecastSchedule(valuation: ValuationOverviewResponse | null, growthDelta: number): number[] | null {
  const dcfInputs = valuation?.frameworks.find((framework) => framework.id === 'dcf')?.inputs || null
  const source = typeof dcfInputs?.source === 'string' ? dcfInputs.source : ''
  if (source !== 'ml_forecast' || !Array.isArray(dcfInputs?.annual_net_cash_flows)) return null
  const cashFlows = dcfInputs.annual_net_cash_flows
    .map((value) => (typeof value === 'number' && Number.isFinite(value) ? value : null))
    .filter((value): value is number => value != null)
  if (!cashFlows.length) return null
  if (!growthDelta) return cashFlows
  return cashFlows.map((cf, idx) => cf * ((1 + growthDelta) ** (idx + 1)))
}

function computeLocalFrameworks(
  valuation: ValuationOverviewResponse,
  baseInputs: ValuationBaseInputs,
  scenario: ValuationScenario,
  baseScenario: ValuationScenario,
): ValuationFrameworkResult[] {
  const growthDelta = scenario.forecastGrowth - baseScenario.forecastGrowth
  const baseAssumptions = valuation.assumptions
  const assumptions = {
    ...baseAssumptions,
    forecast_growth: clampNumber(baseAssumptions.forecast_growth + growthDelta, -0.1, 0.4),
    wacc: Math.max(0.01, scenario.wacc),
    ebitda_multiple: Math.max(0.1, scenario.ebitdaMultiple),
    earnings_multiple: Math.max(0.1, scenario.earningsMultiple),
    terminal_growth: Math.min(scenario.terminalGrowth, Math.max(0.0001, scenario.wacc - 0.02)),
  }
  const simInputs: ValuationBaseInputs = { ...baseInputs }
  if (isFiniteNumber(baseInputs.revenue_growth)) {
    simInputs.revenue_growth = baseInputs.revenue_growth + growthDelta
  } else {
    simInputs.revenue_growth = growthDelta
  }
  if (scenario.ebitdaMargin != null && baseScenario.ebitdaMargin != null && isFiniteNumber(baseInputs.revenue_ttm)) {
    simInputs.ebitda_margin = scenario.ebitdaMargin
    simInputs.ebitda_ttm = baseInputs.revenue_ttm * scenario.ebitdaMargin
  }

  const earningsMid = isFiniteNumber(simInputs.net_income_ttm) && simInputs.net_income_ttm > 0
    ? simInputs.net_income_ttm * assumptions.earnings_multiple
    : null
  const earningsRange = rangeAroundMid(earningsMid, 0.8, 1.2)

  const ebitdaEnterprise = isFiniteNumber(simInputs.ebitda_ttm) && simInputs.ebitda_ttm > 0
    ? simInputs.ebitda_ttm * assumptions.ebitda_multiple
    : null
  const ebitdaEquity = ebitdaEnterprise != null ? equityFromEnterprise(ebitdaEnterprise, simInputs) : null
  const ebitdaEnterpriseRange = rangeAroundMid(ebitdaEnterprise, 0.85, 1.15)
  const ebitdaEquityRange = rangeAroundMid(ebitdaEquity, 0.85, 1.15)

  const revenueEnterprise = isFiniteNumber(simInputs.revenue_ttm) && simInputs.revenue_ttm > 0
    ? simInputs.revenue_ttm * assumptions.revenue_multiple
    : null
  const revenueEquity = revenueEnterprise != null ? equityFromEnterprise(revenueEnterprise, simInputs) : null
  const revenueEnterpriseRange = rangeAroundMid(revenueEnterprise, 0.8, 1.2)
  const revenueEquityRange = rangeAroundMid(revenueEquity, 0.8, 1.2)

  const forecastSchedule = dcfForecastSchedule(valuation, growthDelta)
  const dcfEnterprise = forecastSchedule
    ? computeDcfSchedule(forecastSchedule, assumptions.terminal_growth, assumptions.wacc)
    : computeDcf(simInputs.ebitda_ttm, assumptions.forecast_growth, assumptions.forecast_years, assumptions.terminal_growth, assumptions.wacc)
  const dcfEquity = dcfEnterprise != null ? equityFromEnterprise(dcfEnterprise, simInputs) : null
  const dcfEnterpriseRange = rangeAroundMid(dcfEnterprise, 0.82, 1.18)
  const dcfEquityRange = rangeAroundMid(dcfEquity, 0.82, 1.18)

  const baseFrameworkById = new Map(valuation.frameworks.map((framework) => [framework.id, framework]))
  const mergeFramework = (
    id: string,
    label: string,
    enterpriseRange: ReturnType<typeof rangeAroundMid>,
    equityRange: ReturnType<typeof rangeAroundMid>,
  ): ValuationFrameworkResult => {
    const base = baseFrameworkById.get(id)
    return {
      id,
      label: base?.label || label,
      method_summary: base?.method_summary || '',
      reconciliation_weight: base?.reconciliation_weight ?? null,
      reconciliation_note: base?.reconciliation_note || '',
      enterprise_value_low: enterpriseRange.low,
      enterprise_value_mid: enterpriseRange.mid,
      enterprise_value_high: enterpriseRange.high,
      enterprise_value: enterpriseRange.mid,
      equity_value_low: equityRange.low,
      equity_value_mid: equityRange.mid,
      equity_value_high: equityRange.high,
      equity_value: equityRange.mid,
      inputs: base?.inputs || {},
      assumptions_text: base?.assumptions_text || [],
    }
  }

  return [
    mergeFramework('earnings_multiple', 'Owner Earnings Multiple', { low: null, mid: null, high: null }, earningsRange),
    mergeFramework('ebitda_multiple', 'EBITDA Multiple', ebitdaEnterpriseRange, ebitdaEquityRange),
    mergeFramework('revenue_multiple', 'Revenue Multiple', revenueEnterpriseRange, revenueEquityRange),
    mergeFramework('dcf', 'Discounted Cash Flow (DCF)', dcfEnterpriseRange, dcfEquityRange),
  ]
}

export function equityValueRange(frameworks: ValuationFrameworkResult[], weightById?: Record<string, number>) {
  const rows = frameworks
    .map((f) => {
      const low = f.equity_value_low ?? f.equity_value
      const mid = f.equity_value_mid ?? f.equity_value
      const high = f.equity_value_high ?? f.equity_value
      const weight = weightById ? weightById[f.id] ?? 0 : f.reconciliation_weight ?? 1
      return isFiniteNumber(low) && isFiniteNumber(mid) && isFiniteNumber(high) && isFiniteNumber(weight) && weight > 0
        ? { low, mid, high, weight }
        : null
    })
    .filter((row): row is { low: number; mid: number; high: number; weight: number } => row != null)
  if (!rows.length) return { low: null, mid: null, high: null }
  const totalWeight = rows.reduce((sum, row) => sum + row.weight, 0)
  if (!isFiniteNumber(totalWeight) || totalWeight <= 0) return { low: null, mid: null, high: null }
  const weighted = (key: 'low' | 'mid' | 'high') => rows.reduce((sum, row) => sum + row[key] * row.weight, 0) / totalWeight
  return {
    low: roundCurrency(weighted('low')),
    mid: roundCurrency(weighted('mid')),
    high: roundCurrency(weighted('high')),
  }
}

type EquityBand = { low: number | null; mid: number | null; high: number | null }
type ValuationBand = EquityBand
type MethodEnterpriseBand = ValuationBand & { inferred: boolean }

function bridgeEquityBandToEnterprise(band: ValuationBand, base: ValuationBaseInputs): ValuationBand {
  return {
    low: isFiniteNumber(band.low) ? roundCurrency(enterpriseFromEquity(band.low, base)) : null,
    mid: isFiniteNumber(band.mid) ? roundCurrency(enterpriseFromEquity(band.mid, base)) : null,
    high: isFiniteNumber(band.high) ? roundCurrency(enterpriseFromEquity(band.high, base)) : null,
  }
}

export function frameworkEnterpriseValueRange(framework: ValuationFrameworkResult, base: ValuationBaseInputs): MethodEnterpriseBand {
  const explicit = {
    low: framework.enterprise_value_low ?? framework.enterprise_value ?? null,
    mid: framework.enterprise_value_mid ?? framework.enterprise_value ?? null,
    high: framework.enterprise_value_high ?? framework.enterprise_value ?? null,
  }
  if (isFiniteNumber(explicit.mid)) return { ...explicit, inferred: false }
  const inferred = bridgeEquityBandToEnterprise({
    low: framework.equity_value_low ?? framework.equity_value,
    mid: framework.equity_value_mid ?? framework.equity_value,
    high: framework.equity_value_high ?? framework.equity_value,
  }, base)
  return { ...inferred, inferred: inferred.mid != null }
}

export function inferredEnterpriseRangeFromEquityRange(range: ValuationBand, base: ValuationBaseInputs): MethodEnterpriseBand {
  const inferred = bridgeEquityBandToEnterprise(range, base)
  return { ...inferred, inferred: inferred.mid != null }
}

/** Blend per-method equity bands with a weight map (normalizing over the methods
 * that have a value and a positive weight) — mirrors `equityValueRange`, but for
 * the per-method history/bridge ingredients the server ships. */
export function blendMethodEquities(methods: MethodEquity[], weightById: Record<string, number>): EquityBand {
  const rows = methods
    .map((m) => {
      const low = m.equity_value_low ?? m.equity_value_mid
      const mid = m.equity_value_mid
      const high = m.equity_value_high ?? m.equity_value_mid
      const weight = weightById[m.id] ?? 0
      return isFiniteNumber(low) && isFiniteNumber(mid) && isFiniteNumber(high) && weight > 0
        ? { low, mid, high, weight }
        : null
    })
    .filter((row): row is { low: number; mid: number; high: number; weight: number } => row != null)
  if (!rows.length) return { low: null, mid: null, high: null }
  const total = rows.reduce((sum, row) => sum + row.weight, 0)
  if (!(total > 0)) return { low: null, mid: null, high: null }
  const weighted = (key: 'low' | 'mid' | 'high') => rows.reduce((sum, row) => sum + row[key] * row.weight, 0) / total
  return {
    low: roundCurrency(weighted('low')),
    mid: roundCurrency(weighted('mid')),
    high: roundCurrency(weighted('high')),
  }
}

/** Default per-method blend weights (0–100 scale) from the history payload, else
 * from the framework reconciliation weights. */
export function defaultBlendWeights(valuation: ValuationOverviewResponse | null): Record<string, number> {
  const out: Record<string, number> = {}
  const fromHistory = valuation?.history?.default_weights
  if (fromHistory && fromHistory.length) {
    for (const w of fromHistory) {
      if (isFiniteNumber(w.weight)) out[w.id] = Math.round(w.weight * 100)
    }
  } else {
    for (const f of valuation?.frameworks || []) {
      if (isFiniteNumber(f.reconciliation_weight)) out[f.id] = Math.round((f.reconciliation_weight as number) * 100)
    }
  }
  for (const method of VALUATION_BLEND_METHODS) {
    if (!(method.id in out)) out[method.id] = 0
  }
  return out
}

/** Normalized contribution (0–1) of each method given the raw weight map. */
export function normalizedWeightShare(weights: Record<string, number>, id: string): number {
  const total = VALUATION_BLEND_METHODS.reduce((sum, m) => sum + Math.max(0, weights[m.id] ?? 0), 0)
  if (!(total > 0)) return 0
  return Math.max(0, weights[id] ?? 0) / total
}

function weightsEqual(a: Record<string, number>, b: Record<string, number>): boolean {
  return VALUATION_BLEND_METHODS.every((m) => (a[m.id] ?? 0) === (b[m.id] ?? 0))
}

function formatMonthLabel(period: string | null | undefined): string {
  const raw = trimStr(period)
  if (!raw) return ''
  const date = new Date(`${raw.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(date.getTime())) return raw
  return date.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
}

function localAppliedChanges(deltas: ValuationSimulationRequest): string[] {
  const applied: string[] = []
  if (isFiniteNumber(deltas.revenue_growth_delta_pct) && Math.abs(deltas.revenue_growth_delta_pct) > 0.00001) {
    applied.push(`Revenue growth ${deltas.revenue_growth_delta_pct >= 0 ? '+' : ''}${(deltas.revenue_growth_delta_pct * 100).toFixed(0)} pts`)
  }
  if (isFiniteNumber(deltas.ebitda_margin_delta_pts) && Math.abs(deltas.ebitda_margin_delta_pts) > 0.00001) {
    applied.push(`EBITDA margin ${deltas.ebitda_margin_delta_pts >= 0 ? '+' : ''}${(deltas.ebitda_margin_delta_pts * 100).toFixed(1)} pts`)
  }
  if (isFiniteNumber(deltas.wacc_delta_pct) && Math.abs(deltas.wacc_delta_pct) > 0.00001) {
    applied.push(`WACC ${deltas.wacc_delta_pct >= 0 ? '+' : ''}${(deltas.wacc_delta_pct * 100).toFixed(1)} pts`)
  }
  if (isFiniteNumber(deltas.ebitda_multiple_delta) && Math.abs(deltas.ebitda_multiple_delta) > 0.00001) {
    applied.push(`EBITDA multiple ${deltas.ebitda_multiple_delta >= 0 ? '+' : ''}${deltas.ebitda_multiple_delta.toFixed(1)}x`)
  }
  if (isFiniteNumber(deltas.earnings_multiple_delta) && Math.abs(deltas.earnings_multiple_delta) > 0.00001) {
    applied.push(`Owner earnings multiple ${deltas.earnings_multiple_delta >= 0 ? '+' : ''}${deltas.earnings_multiple_delta.toFixed(1)}x`)
  }
  if (isFiniteNumber(deltas.terminal_growth_delta_pct) && Math.abs(deltas.terminal_growth_delta_pct) > 0.00001) {
    applied.push(`Terminal growth ${deltas.terminal_growth_delta_pct >= 0 ? '+' : ''}${(deltas.terminal_growth_delta_pct * 100).toFixed(1)} pts`)
  }
  return applied
}

function runLocalSimulation(
  valuation: ValuationOverviewResponse | null,
  scenario: ValuationScenario | null,
  baseScenario: ValuationScenario | null,
): ValuationSimulationResponse | null {
  if (!valuation || !scenario || !baseScenario) return null
  const deltas = scenarioDiff(baseScenario, scenario)
  if (!hasScenarioChanges(deltas)) return null
  const frameworks = computeLocalFrameworks(valuation, valuation.base_inputs, scenario, baseScenario)
  const range = equityValueRange(frameworks)
  const baseRange = equityValueRange(valuation.frameworks)
  return {
    workbook_id: valuation.workbook_id,
    as_of: valuation.as_of,
    valuation_set_version: valuation.valuation_set_version,
    signature: `${valuation.signature}:local-scenario`,
    base_assumptions: valuation.assumptions,
    simulated_assumptions: {
      ...valuation.assumptions,
      forecast_growth: clampNumber(valuation.assumptions.forecast_growth + (deltas.revenue_growth_delta_pct ?? 0), -0.1, 0.4),
      wacc: Math.max(0.01, scenario.wacc),
      ebitda_multiple: Math.max(0.1, scenario.ebitdaMultiple),
      earnings_multiple: Math.max(0.1, scenario.earningsMultiple),
      terminal_growth: Math.min(scenario.terminalGrowth, Math.max(0.0001, scenario.wacc - 0.02)),
    },
    base_inputs: valuation.base_inputs,
    simulated_inputs: frameworks.length ? computeLocalFrameworkInputs(valuation.base_inputs, scenario, baseScenario, deltas) : valuation.base_inputs,
    frameworks: frameworks.map((framework) => {
      const base = valuation.frameworks.find((item) => item.id === framework.id)
      const baseEquity = base?.equity_value ?? null
      const simulatedEquity = framework.equity_value ?? null
      const deltaAbs = isFiniteNumber(baseEquity) && isFiniteNumber(simulatedEquity) ? roundCurrency(simulatedEquity - baseEquity) : null
      return {
        id: framework.id,
        label: framework.label,
        base_equity_value: baseEquity,
        simulated_equity_value: simulatedEquity,
        delta_abs: deltaAbs,
        delta_pct: isFiniteNumber(baseEquity) && baseEquity !== 0 && isFiniteNumber(simulatedEquity)
          ? roundCurrency((simulatedEquity / baseEquity - 1) * 10000) / 10000
          : null,
      }
    }),
    simulated_equity_value_low: range.low,
    simulated_equity_value_mid: range.mid,
    simulated_equity_value_high: range.high,
    base_equity_value_mid: baseRange.mid,
    applied_changes: localAppliedChanges(deltas),
    warnings: valuation.warnings,
  }
}

function computeLocalFrameworkInputs(
  baseInputs: ValuationBaseInputs,
  scenario: ValuationScenario,
  baseScenario: ValuationScenario,
  deltas: ValuationSimulationRequest,
): ValuationBaseInputs {
  const simInputs: ValuationBaseInputs = { ...baseInputs }
  if (isFiniteNumber(deltas.revenue_growth_delta_pct)) {
    simInputs.revenue_growth = (baseInputs.revenue_growth ?? 0) + deltas.revenue_growth_delta_pct
  }
  if (scenario.ebitdaMargin != null && baseScenario.ebitdaMargin != null && isFiniteNumber(baseInputs.revenue_ttm)) {
    simInputs.ebitda_margin = scenario.ebitdaMargin
    simInputs.ebitda_ttm = baseInputs.revenue_ttm * scenario.ebitdaMargin
  }
  return simInputs
}

export function buildScenarioFootballMethods(methods: FootballMethod[], sim: ValuationSimulationResponse | null, localFrameworks: ValuationFrameworkResult[] = []): FootballMethod[] {
  const localById = new Map(localFrameworks.map((framework) => [framework.id, framework]))
  // `sim` is only present once a slider actually moved, so we use it as the
  // "scenario active" gate: at rest each method keeps the server's exact numbers
  // (and its base applicable/unused flag). Once a scenario is live, every method
  // is re-derived from the same local frameworks the simulator uses — so a method
  // that was unused at base can light up when the scenario makes it valid, and a
  // method that turns invalid (e.g. EBITDA forced negative) drops to unused.
  const scenarioActive = sim != null
  return methods.map((method) => {
    if (method.id === 'blended') {
      if (sim) {
        return {
          ...method,
          low: sim.simulated_equity_value_low ?? method.low,
          mid: sim.simulated_equity_value_mid ?? method.mid,
          high: sim.simulated_equity_value_high ?? method.high,
          applicable: true,
        }
      }
      return method
    }
    if (!scenarioActive) return method
    const local = localById.get(method.id)
    if (!local) return method
    const rawMid = local.equity_value_mid ?? local.equity_value ?? null
    if (rawMid == null || !Number.isFinite(rawMid)) {
      return {
        ...method,
        applicable: false,
        unusedReason: method.unusedReason ?? `Not used — ${methodUnavailableReason(method.id)}`,
      }
    }
    return {
      ...method,
      low: local.equity_value_low ?? rawMid,
      mid: rawMid,
      high: local.equity_value_high ?? rawMid,
      applicable: true,
      unusedReason: undefined,
    }
  })
}

function valuationIndustryPhrase(valuation: ValuationOverviewResponse | null): string {
  const title = trimStr(valuation?.personalization_context?.industry?.industry_title || valuation?.personalization_context?.company?.inferred_industry || '')
  if (!title) return 'business'
  if (/\b(law|lawyer|legal|attorney)\b/i.test(title)) return 'legal-services practice'
  if (/\b(accounting|bookkeeping|tax)\b/i.test(title)) return 'accounting practice'
  if (/\b(consult|professional)\b/i.test(title)) return 'professional-services business'
  return `${title.toLowerCase()} business`
}

function valuationServiceFocus(valuation: ValuationOverviewResponse | null): string {
  const company = valuation?.personalization_context?.company
  const fromServices = firstContextLabel(company?.services_json)
  if (fromServices) return ` around ${fromServices}`
  const fromSegments = firstContextLabel(company?.customer_segments_json)
  if (fromSegments) return ` for ${fromSegments}`
  return ''
}

function firstContextLabel(value: unknown): string {
  if (Array.isArray(value)) {
    for (const item of value) {
      const label = firstContextLabel(item)
      if (label) return label
    }
    return ''
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    for (const key of ['label', 'name', 'title', 'segment', 'service', 'description']) {
      const label = trimStr(record[key])
      if (label) return label
    }
  }
  const text = trimStr(value)
  return text.length <= 60 ? text : ''
}

function driverImpactText(driver: ValueDriver | null | undefined): string {
  if (!driver || !isFiniteNumber(driver.value_impact)) return ''
  return formatSignedCurrency(driver.value_impact)
}

function valuationFocusInsight(valuation: ValuationOverviewResponse | null, drivers: ValueDriver[]): string {
  if (!valuation) return 'Once the valuation artifact loads, this read will call out the highest-return lever and the first actions that make the estimate easier to defend.'
  const top = drivers[0] || null
  const margin = drivers.find((driver) => driver.id === 'margin')
  const industry = valuationIndustryPhrase(valuation)
  const focus = valuationServiceFocus(valuation)
  const topImpact = driverImpactText(top)
  const marginImpact = driverImpactText(margin)
  const ebitdaMargin = isFiniteNumber(valuation.base_inputs.ebitda_margin)
    ? ` EBITDA margin is ${formatPercentPoint(valuation.base_inputs.ebitda_margin, 1)}, so`
    : ''
  if (top?.id === 'growth') {
    return `Growth is the biggest value lever here${topImpact ? ` (${topImpact} in the modeled nudge)` : ''}, but it only helps if it is quality growth.${ebitdaMargin} for this ${industry}${focus}, I’d focus first on repeatable work, firmer pricing, and cutting low-realization volume before adding more demand. That makes the DCF stronger and gives a buyer a cleaner story.`
  }
  if (top?.id === 'margin') {
    return `The fastest value lift is margin${topImpact ? ` (${topImpact} in the modeled nudge)` : ''}. For this ${industry}${focus}, I’d tighten the work mix first: raise price where outcomes justify it, standardize delivery on repeatable services, and prune clients or projects that absorb partner time without enough contribution.`
  }
  if (top?.id === 'risk') {
    return `The value story is most sensitive to risk and cost of capital. For this ${industry}${focus}, I’d make revenue more durable before chasing a higher multiple: reduce customer concentration, document repeatable pipeline, and show a few months of steady cash conversion so buyers discount the future less aggressively.`
  }
  return `The clearest path to a higher value is making owner earnings more durable. For this ${industry}${focus}, I’d focus on margin discipline${marginImpact ? ` (${marginImpact} sensitivity)` : ''}, repeatable revenue, and clean cash conversion; those are the pieces that make both the multiple view and DCF easier to defend.`
}

function driverById(drivers: ValueDriver[], id: string): ValueDriver | null {
  return drivers.find((driver) => driver.id === id) || null
}

function driverDescription(driver: ValueDriver | null, fallback: string): string {
  if (!driver) return fallback
  return `${driver.current_state} ${driver.explanation}`
}

function driverImpactLabel(driver: ValueDriver | null): string {
  if (!driver) return ''
  const impact = driver.value_impact != null
    ? formatSignedCurrency(driver.value_impact)
    : 'qualitative'
  return `${driver.lever}: ${impact}`
}

/**
 * Calm, additive one-liner shown beneath the valuation headline when the AI has
 * excluded one-time / non-recurring items from normalized earnings. Renders
 * nothing when no normalization is present, so the headline is unchanged.
 */
function ValuationAiNormalizationRow({
  normalization,
}: {
  normalization: ValuationAiNormalization | null | undefined
}) {
  if (!normalization) return null
  const { excluded_total, reported_ebitda, adjusted_ebitda, adjusted_equity_low, adjusted_equity_high } = normalization
  return (
    <p
      style={{
        marginTop: 'var(--s3, 8px)',
        padding: 'var(--s2, 6px) var(--s3, 8px)',
        borderRadius: 'var(--radius-sm, 6px)',
        border: '1px solid var(--border, rgba(127,127,127,0.25))',
        background: 'var(--surface-2, rgba(127,127,127,0.06))',
        color: 'var(--muted, #6b7280)',
        fontSize: '0.85em',
        lineHeight: 1.45,
      }}
    >
      <span aria-hidden="true" style={{ marginRight: '0.4em' }}>✨</span>
      AI excluded {formatSignedCurrency(excluded_total)} one-time → adjusted EBITDA{' '}
      <strong style={{ color: 'var(--text, inherit)' }}>{formatCurrencyCompact(adjusted_ebitda)}</strong>
      {reported_ebitda != null ? <> (was {formatCurrencyCompact(reported_ebitda)})</> : null}
      {adjusted_equity_low != null && adjusted_equity_high != null ? (
        <>
          ; adjusted value{' '}
          <strong style={{ color: 'var(--text, inherit)' }}>
            {formatCurrencyCompact(adjusted_equity_low)}–{formatCurrencyCompact(adjusted_equity_high)}
          </strong>
        </>
      ) : null}
    </p>
  )
}

function ValuationBody({
  loading,
  error,
  valuation,
  methods,
  drivers,
  asOfLabel,
  blendedMid,
  heroHeadline,
  heroSubtext,
  explainDriver,
  explainHistory,
}: ValuationBodyProps) {
  const [scenario, setScenario] = useState<ValuationScenario | null>(() => buildBaseScenario(valuation))
  const baseScenario = useMemo(() => buildBaseScenario(valuation), [valuation])

  const defaultWeights = useMemo(() => defaultBlendWeights(valuation), [valuation])
  const [weights, setWeights] = useState<Record<string, number>>(() => defaultBlendWeights(valuation))

  useEffect(() => {
    const nextBase = buildBaseScenario(valuation)
    setScenario(nextBase)
    setWeights(defaultBlendWeights(valuation))
  }, [valuation])

  const updateScenario = useCallback(
    (patch: Partial<ValuationScenario>) => {
      setScenario((prev) => {
        const base = prev || baseScenario
        if (!base) return prev
        return { ...base, ...patch }
      })
    },
    [baseScenario],
  )

  const resetScenario = useCallback(() => {
    const nextBase = buildBaseScenario(valuation)
    setScenario(nextBase)
  }, [valuation])

  const updateWeight = useCallback((id: string, value: number) => {
    setWeights((prev) => ({ ...prev, [id]: Math.max(0, Math.min(100, value)) }))
  }, [])
  const resetWeights = useCallback(() => setWeights(defaultBlendWeights(valuation)), [valuation])
  const weightsChanged = useMemo(() => !weightsEqual(weights, defaultWeights), [weights, defaultWeights])

  const sim = useMemo(() => runLocalSimulation(valuation, scenario, baseScenario), [baseScenario, scenario, valuation])
  const localFrameworks = useMemo(
    () => valuation && scenario && baseScenario ? computeLocalFrameworks(valuation, valuation.base_inputs, scenario, baseScenario) : [],
    [baseScenario, scenario, valuation],
  )
  const simMid = sim?.simulated_equity_value_mid ?? null
  const baseMid = sim?.base_equity_value_mid ?? null
  const simDelta = simMid != null && baseMid != null ? simMid - baseMid : null
  const scenarioDeltas = scenarioDiff(baseScenario, scenario)
  const adjustedRange = sim?.simulated_equity_value_low != null && sim?.simulated_equity_value_high != null
    ? `${formatCurrencyCompact(sim.simulated_equity_value_low)}–${formatCurrencyCompact(sim.simulated_equity_value_high)}`
    : null
  const personalizationLabel = valuationPersonalizationLabel(valuation)
  const scenarioMethods = useMemo(() => buildScenarioFootballMethods(methods, sim, localFrameworks), [localFrameworks, methods, sim])
  const scenarioChanged = hasScenarioChanges(scenarioDeltas)
  // Re-blend the current valuation with the active method weights (client-side,
  // instant). At rest (default weights, no scenario) we keep the server's exact
  // numbers; once the user moves a weight or slider we recompute from the same
  // per-method frameworks the football field shows.
  const weightedBlend = useMemo(() => {
    const fw = localFrameworks.length ? localFrameworks : valuation?.frameworks || []
    return equityValueRange(fw, weights)
  }, [localFrameworks, valuation, weights])
  const useClientBlend = weightsChanged || scenarioChanged
  const displayMethods = useMemo(() => {
    if (!useClientBlend || weightedBlend.mid == null) return scenarioMethods
    return scenarioMethods.map((method) =>
      method.id === 'blended'
        ? {
            ...method,
            low: weightedBlend.low ?? method.low,
            mid: weightedBlend.mid ?? method.mid,
            high: weightedBlend.high ?? method.high,
          }
        : method,
    )
  }, [scenarioMethods, useClientBlend, weightedBlend])
  const activeBlendedMid = useClientBlend ? weightedBlend.mid ?? simMid ?? blendedMid : simMid ?? blendedMid
  const activeHeadline = activeBlendedMid != null
    ? `Valuation estimate: ${formatCurrencyCompact(activeBlendedMid)}`
    : heroHeadline
  const activeSubtext = adjustedRange && simDelta != null
    ? `Scenario range is ${adjustedRange}, a ${formatSignedCurrency(simDelta)} move from the base midpoint. The chart is recalculated from the same valuation engine as the sliders.`
    : heroSubtext
  const growthDriver = driverById(drivers, 'growth')
  const marginDriver = driverById(drivers, 'margin')
  const riskDriver = driverById(drivers, 'risk')

  // Tag the valuation range into the board pack as a grounded text caption
  // (no football-field block kind exists; the caption carries the ranges).
  const valuationReportPayload = (): AddToReportPayload => {
    const blended = activeBlendedMid != null
      ? `Blended equity value ≈ ${formatCurrencyCompact(activeBlendedMid)}.`
      : ''
    const methods = displayMethods
      .filter((method) => method.applicable !== false)
      .map((method) => `${method.label}: ${formatCurrencyCompact(method.low)}–${formatCurrencyCompact(method.high)} (mid ${formatCurrencyCompact(method.mid)})`)
      .join('; ')
    return {
      title: 'Valuation summary',
      kind: 'text',
      section: 'home',
      source: { metric: 'valuation_summary' },
      text: `${blended} Method ranges — ${methods}.`.trim(),
    }
  }

  return (
    <>
      <section className="hero valuation-hero rise">
        <div className="hero-top">
          <div>
            <h1>{activeHeadline}</h1>
            <p>{activeSubtext}</p>
            <ValuationAiNormalizationRow normalization={valuation?.ai_normalization} />
          </div>
        </div>
        <figure className="figure valuation-primary-figure">
          <div className="valuation-figure-header">
            <div className="valuation-figure-title" aria-hidden="true" />
            {displayMethods.length ? <PinToReportButton buildPayload={valuationReportPayload} /> : null}
            {scenario && baseScenario ? (
              <div className="valuation-figure-scenario">
                <div>
                  <span className="tag">{hasScenarioChanges(scenarioDeltas) ? 'Scenario view' : 'Base view'}</span>
                </div>
                <button type="button" className="btn ghost" onClick={resetScenario} disabled={!hasScenarioChanges(scenarioDeltas)}>
                  Reset
                </button>
              </div>
            ) : null}
          </div>
          {loading ? (
            <CfoChartSkeleton label="Loading valuation range chart…" />
          ) : displayMethods.length ? (
            <FootballField
              className="fig-svg"
              ariaLabel="Football-field range chart of estimated equity value by method"
              methods={displayMethods}
              blended={activeBlendedMid}
              formatValue={(v) => formatCurrencyCompact(v)}
            />
          ) : (
            <p style={{ color: 'var(--muted)' }}>
              {error || 'The valuation isn’t available for this workbook yet.'}
            </p>
          )}

          {loading ? (
            <CfoCardSkeletons count={1} label="Loading valuation simulator…" />
          ) : scenario && baseScenario ? (
            <div className="valuation-simulator integrated">
              <div className="valuation-slider-grid">
                <ValuationSlider
                  label="Forecast growth"
                  description={driverDescription(growthDriver, 'Durable growth compounds future cash flows and mainly moves the DCF cross-check.')}
                  impact={driverImpactLabel(growthDriver)}
                  value={scenario.forecastGrowth}
                  baseValue={baseScenario.forecastGrowth}
                  min={Math.min(-0.1, baseScenario.forecastGrowth - 0.1)}
                  max={Math.max(0.4, baseScenario.forecastGrowth + 0.15)}
                  step={0.005}
                  formatValue={(value) => formatPercentPoint(value, 1)}
                  formatDelta={formatSignedPercentPointDelta}
                  onChange={(forecastGrowth) => updateScenario({ forecastGrowth })}
                />
                <ValuationSlider
                  label="EBITDA margin"
                  description={driverDescription(marginDriver, 'Margin turns revenue into EBITDA, which directly changes the EBITDA multiple method.')}
                  impact={driverImpactLabel(marginDriver)}
                  value={scenario.ebitdaMargin ?? 0}
                  baseValue={baseScenario.ebitdaMargin ?? 0}
                  min={Math.min(-0.1, (baseScenario.ebitdaMargin ?? 0) - 0.1)}
                  max={Math.max(0.5, (baseScenario.ebitdaMargin ?? 0) + 0.15)}
                  step={0.005}
                  disabled={scenario.ebitdaMargin == null || baseScenario.ebitdaMargin == null}
                  disabledText="Needs EBITDA and revenue"
                  formatValue={(value) => formatPercentPoint(value, 1)}
                  formatDelta={formatSignedPercentPointDelta}
                  onChange={(ebitdaMargin) => updateScenario({ ebitdaMargin })}
                />
                <ValuationSlider
                  label="Cost of capital"
                  description={driverDescription(riskDriver, 'Lower business risk reduces the discount rate and makes future cash flows worth more today.')}
                  impact={driverImpactLabel(riskDriver)}
                  value={scenario.wacc}
                  baseValue={baseScenario.wacc}
                  min={Math.min(0.06, baseScenario.wacc - 0.04)}
                  max={Math.max(0.24, baseScenario.wacc + 0.06)}
                  step={0.005}
                  formatValue={(value) => formatPercentPoint(value, 1)}
                  formatDelta={formatSignedPercentPointDelta}
                  inverse
                  onChange={(wacc) => updateScenario({ wacc })}
                />
                <ValuationSlider
                  label="EBITDA multiple"
                  description="This is the buyer's market multiple on operating profit; stronger growth, cleaner margins, and lower concentration support a higher multiple."
                  impact="Market multiple method"
                  value={scenario.ebitdaMultiple}
                  baseValue={baseScenario.ebitdaMultiple}
                  min={Math.max(0.5, baseScenario.ebitdaMultiple - 3)}
                  max={baseScenario.ebitdaMultiple + 5}
                  step={0.1}
                  formatValue={(value) => `${value.toFixed(1)}x`}
                  formatDelta={formatSignedMultipleDelta}
                  onChange={(ebitdaMultiple) => updateScenario({ ebitdaMultiple })}
                />
                <ValuationSlider
                  label="Owner earnings multiple"
                  description="This anchors the small-business/SDE view; it is most useful once owner add-backs and one-time costs are normalized."
                  impact="Market multiple method"
                  value={scenario.earningsMultiple}
                  baseValue={baseScenario.earningsMultiple}
                  min={Math.max(0.1, baseScenario.earningsMultiple - 1.5)}
                  max={baseScenario.earningsMultiple + 4}
                  step={0.1}
                  formatValue={(value) => `${value.toFixed(1)}x`}
                  formatDelta={formatSignedMultipleDelta}
                  onChange={(earningsMultiple) => updateScenario({ earningsMultiple })}
                />
                <ValuationSlider
                  label="Terminal growth"
                  description="This is the long-run growth assumption inside the DCF terminal value, so small changes can move the DCF line a lot."
                  impact="DCF-only assumption"
                  value={scenario.terminalGrowth}
                  baseValue={baseScenario.terminalGrowth}
                  min={0}
                  max={Math.max(0.05, baseScenario.terminalGrowth + 0.02)}
                  step={0.0025}
                  formatValue={(value) => formatPercentPoint(value, 2)}
                  formatDelta={formatSignedPercentPointDelta}
                  onChange={(terminalGrowth) => updateScenario({ terminalGrowth })}
                />
              </div>

              <div className="valuation-simulator-footer">
                <div>
                  {sim?.applied_changes?.length ? <span>{sim.applied_changes.join(' · ')}</span> : null}
                </div>
                {drivers.length ? (
                  <button type="button" className="btn primary" onClick={() => explainDriver(drivers[0])}>
                    <Sparkles size={14} /> Ask AI where to focus
                  </button>
                ) : null}
              </div>

            </div>
          ) : (
            <div className="cfo-shell-notice">{error || 'The valuation simulator is not available for this workbook yet.'}</div>
          )}
          <CfoAiRead>
            <span className="ff-guide"><b>Where I’d focus:</b></span>{' '}
            {valuationFocusInsight(valuation, drivers)}
          </CfoAiRead>
        </figure>
      </section>

          <ValuationHistorySection
            loading={loading}
            error={error}
            history={valuation?.history ?? null}
            weights={weights}
            explainHistory={explainHistory}
          />

          <ValuationDetailPanels
            loading={loading}
            error={error}
            valuation={valuation}
            frameworks={localFrameworks.length ? localFrameworks : valuation?.frameworks || []}
            weights={weights}
            defaultWeights={defaultWeights}
            weightsChanged={weightsChanged}
            onWeightChange={updateWeight}
            onResetWeights={resetWeights}
          />
    </>
  )
}

type ValuationHistorySectionProps = {
  loading: boolean
  error: string
  history: ValuationHistory | null
  weights: Record<string, number>
  explainHistory: (payload: {
    bridge: ValuationBridge
    bridgeLabel: string
    changeText: string
    weights: Record<string, number>
  }) => void
}

function bridgeBlendedMid(bridge: ValuationBridge, stageId: string, weights: Record<string, number>): number | null {
  const stage = bridge.stages.find((s) => s.id === stageId)
  if (!stage) return null
  return blendMethodEquities(stage.methods, weights).mid
}

export type BridgeView = {
  start: number
  end: number
  change: number
  steps: Array<{ label: string; delta: number }>
}

/** Re-blend a value bridge's per-method stages with the active weights and derive
 * the waterfall (start anchor, three driver steps, end anchor). Returns `null`
 * when the start/end stages can't be blended. Steps always reconcile:
 * `start + Σ delta == end`. Exported for tests. */
export function computeBridgeView(bridge: ValuationBridge, weights: Record<string, number>): BridgeView | null {
  const start = bridgeBlendedMid(bridge, 'start', weights)
  const afterRevenue = bridgeBlendedMid(bridge, 'after_revenue', weights)
  const afterMargin = bridgeBlendedMid(bridge, 'after_margin', weights)
  const end = bridgeBlendedMid(bridge, 'end', weights)
  if (start == null || end == null) return null
  const steps = [
    { label: 'Revenue & growth', delta: afterRevenue != null ? afterRevenue - start : 0 },
    { label: 'Margin/profitability', delta: afterRevenue != null && afterMargin != null ? afterMargin - afterRevenue : 0 },
    { label: 'Cash & debt', delta: afterMargin != null ? end - afterMargin : 0 },
  ]
  return { start, end, steps, change: end - start }
}

function ValuationHistorySection({
  loading,
  error,
  history,
  weights,
  explainHistory,
}: ValuationHistorySectionProps) {
  const [bridgeMode, setBridgeMode] = useState<'mom' | 'yoy'>('yoy')

  const points = useMemo(() => history?.points ?? [], [history])
  const linePoints = useMemo(
    () =>
      points.map((point) => ({
        label: formatMonthLabel(point.period),
        value: blendMethodEquities(point.methods, weights).mid ?? point.equity_value_mid ?? 0,
        low: blendMethodEquities(point.methods, weights).low ?? point.equity_value_low,
        high: blendMethodEquities(point.methods, weights).high ?? point.equity_value_high,
      })),
    [points, weights],
  )
  const axisLabels = useMemo(() => {
    if (!points.length) return []
    const idxs = [0, Math.floor((points.length - 1) / 3), Math.floor((2 * (points.length - 1)) / 3), points.length - 1]
    const unique = Array.from(new Set(idxs.filter((i) => i >= 0 && i < points.length)))
    return unique.map((i) => formatMonthLabel(points[i].period))
  }, [points])

  const availableBridges = useMemo(() => {
    const out: Array<{ id: 'mom' | 'yoy'; bridge: ValuationBridge }> = []
    if (history?.bridge_yoy) out.push({ id: 'yoy', bridge: history.bridge_yoy })
    if (history?.bridge_mom) out.push({ id: 'mom', bridge: history.bridge_mom })
    return out
  }, [history])

  const activeBridge = useMemo(() => {
    const match = availableBridges.find((b) => b.id === bridgeMode)
    return (match ?? availableBridges[0])?.bridge ?? null
  }, [availableBridges, bridgeMode])

  const bridgeView = useMemo(
    () => (activeBridge ? computeBridgeView(activeBridge, weights) : null),
    [activeBridge, weights],
  )

  if (loading) {
    return (
      <section className="valuation-detail-section" aria-label="Modeled value over time">
        <CfoChartSkeleton label="Loading modeled-value history…" />
      </section>
    )
  }
  if (!history || points.length < 2) {
    return null
  }

  const changeText = bridgeView ? formatSignedCurrency(bridgeView.change) : '—'
  const changeDirection = bridgeView ? (bridgeView.change >= 0 ? 'up' : 'down') : 'flat'
  const horizonLabel = (activeBridge?.id ?? bridgeMode) === 'yoy' ? 'the last 12 months' : 'the latest month'

  // Tag the value bridge into the board pack as a grounded text caption.
  const bridgeReportPayload = (): AddToReportPayload => ({
    title: 'Valuation value bridge',
    kind: 'text',
    section: 'home',
    source: { metric: 'valuation_bridge', mode: activeBridge?.id ?? bridgeMode },
    text: bridgeView && activeBridge
      ? `Modeled equity value moved from ${formatCurrencyCompact(bridgeView.start)} (${formatMonthLabel(activeBridge.from_period)}) to ${formatCurrencyCompact(bridgeView.end)} (${formatMonthLabel(activeBridge.to_period)}) — ${changeText} over ${horizonLabel}. Drivers: ${bridgeView.steps.map((step) => `${step.label} ${formatSignedCurrency(step.delta)}`).join('; ')}.`
      : '',
  })

  return (
    <section className="valuation-detail-section valuation-history" aria-label="Modeled value over time">
      <figure className="figure valuation-history-figure">
        <div className="valuation-figure-header">
          <div className="valuation-figure-title">
            <div className="fig-head">
              <span className="ftitle">Modeled value over time</span>
              <span className="fmeta">Trailing 12 month-ends</span>
            </div>
            <p>
              {bridgeView
                ? `Beginning ${formatCurrencyCompact(bridgeView.start)}; ending ${formatCurrencyCompact(bridgeView.end)}; change ${changeText} over ${horizonLabel}.`
                : 'Uses the same methods, multiples, cash, debt, and active method weights as the estimate above.'}
            </p>
          </div>
          <div className="valuation-figure-scenario">
            <div>
              <span className="tag">{changeDirection === 'down' ? 'Value drag' : 'Value build'}</span>
              <h3>{changeText}</h3>
            </div>
          </div>
        </div>

        <TrendLine
          className="fig-svg"
          ariaLabel="Modeled equity value over the trailing twelve month-ends"
          points={linePoints}
          axisLabels={axisLabels}
          formatValue={(v) => formatCurrencyCompact(v)}
          interactive
          showStartValue
        />

        {bridgeView && activeBridge ? (
          <div className="valuation-history-bridge">
            <div className="valuation-history-bridge-head">
              <div className="valuation-history-bridge-title">
                <h3>Value bridge</h3>
                <p>What moved modeled equity value from {formatMonthLabel(activeBridge.from_period)} to {formatMonthLabel(activeBridge.to_period)}.</p>
              </div>
              <div className="valuation-history-toggle" role="tablist" aria-label="Bridge comparison period">
                {availableBridges.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    role="tab"
                    aria-selected={option.id === (activeBridge.id as 'mom' | 'yoy')}
                    className={option.id === (activeBridge.id as 'mom' | 'yoy') ? 'active' : undefined}
                    onClick={() => setBridgeMode(option.id)}
                  >
                    {option.id === 'yoy' ? 'Year over year' : 'Month over month'}
                  </button>
                ))}
              </div>
              <PinToReportButton buildPayload={bridgeReportPayload} />
            </div>
            <WaterfallBridge
              className="fig-svg"
              ariaLabel="Bridge of the modeled value change by driver"
              start={{ label: formatMonthLabel(activeBridge.from_period) || 'Start', value: bridgeView.start }}
              steps={bridgeView.steps}
              end={{ label: formatMonthLabel(activeBridge.to_period) || 'Now', value: bridgeView.end }}
              formatValue={(v) => formatCurrencyCompact(v)}
              interactive
            />
            <div className="valuation-simulator-footer">
              <div>
                <span>{activeBridge.label}: {changeText}</span>
              </div>
              <button
                type="button"
                className="btn primary"
                onClick={() =>
                  explainHistory({
                    bridge: activeBridge,
                    bridgeLabel: activeBridge.label,
                    changeText,
                    weights,
                  })
                }
              >
                <Sparkles size={14} /> Ask AI what’s driving this
              </button>
            </div>
          </div>
        ) : null}
        {!bridgeView ? (
          <p style={{ color: 'var(--muted)' }}>{error || 'Not enough history to break down the value change yet.'}</p>
        ) : null}
      </figure>
    </section>
  )
}

type ValuationWeightControlsProps = {
  weights: Record<string, number>
  defaultWeights: Record<string, number>
  weightsChanged: boolean
  onWeightChange: (id: string, value: number) => void
  onResetWeights: () => void
}

function ValuationWeightControls({
  weights,
  defaultWeights,
  weightsChanged,
  onWeightChange,
  onResetWeights,
}: ValuationWeightControlsProps) {
  return (
    <div className="valuation-weight-controls">
      <div className="valuation-weight-head">
        <span>Adjust method blend</span>
        <button type="button" className="btn ghost" onClick={onResetWeights} disabled={!weightsChanged}>
          Reset
        </button>
      </div>
      <p className="valuation-weight-note">
        Reweight how much each method counts toward the blended estimate. Changes re-blend the range chart, the history
        line, and the bridge instantly.
      </p>
      <div className="valuation-weight-grid">
        {VALUATION_BLEND_METHODS.map((method) => {
          const raw = weights[method.id] ?? 0
          const share = normalizedWeightShare(weights, method.id)
          return (
            <label className="valuation-weight" key={method.id}>
              <span className="valuation-weight-row">
                <span>{method.label}</span>
                <b>{Math.round(share * 100)}%</b>
              </span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={raw}
                aria-label={`${method.label} blend weight`}
                onChange={(event) => onWeightChange(method.id, Number(event.currentTarget.value))}
              />
              <span className="valuation-weight-foot">
                <span>weight</span>
                <span>
                  {raw}
                  {defaultWeights[method.id] != null ? ` · base ${defaultWeights[method.id]}` : ''}
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

type ValuationDetailPanelsProps = {
  loading: boolean
  error: string
  valuation: ValuationOverviewResponse | null
  frameworks: ValuationFrameworkResult[]
  weights: Record<string, number>
  defaultWeights: Record<string, number>
  weightsChanged: boolean
  onWeightChange: (id: string, value: number) => void
  onResetWeights: () => void
}

type ValuationInputTile = {
  id: string
  label: string
  value: string
  note?: string
  detail?: ValuationInputDetail
}

type ValuationInputDetailKind = 'currency' | 'percent' | 'multiple'

type ValuationInputDetail = {
  kind: ValuationInputDetailKind
  formula: string
  description: string
  series: Array<{ period: string; label: string; value: number }>
}

function ValuationDetailPanels({
  loading,
  error,
  valuation,
  frameworks,
  weights,
  defaultWeights,
  weightsChanged,
  onWeightChange,
  onResetWeights,
}: ValuationDetailPanelsProps) {
  const [selectedInputId, setSelectedInputId] = useState<string | null>(null)
  const toggleInput = useCallback((id: string) => {
    setSelectedInputId((current) => current === id ? null : id)
  }, [])

  if (loading) {
    return (
      <section className="valuation-detail-section">
        <CfoCardSkeletons count={2} label="Loading valuation methods and assumptions..." />
      </section>
    )
  }
  if (!valuation) {
    return <div className="cfo-shell-notice">{error || 'Valuation methods and assumptions are not available yet.'}</div>
  }

  const inputTiles = valuationInputTiles(valuation)
  const selectedInput = selectedInputId
    ? inputTiles.find((tile) => tile.id === selectedInputId && tile.detail) || null
    : null
  // Show every blend method in a stable order — including any with no value under
  // the current inputs/scenario. `equityValueRange` still blends only the methods
  // that carry a value and a positive weight, so the unused rows are informational.
  const methodFrameworks = VALUATION_BLEND_METHODS
    .map(({ id }) => frameworks.find((framework) => framework.id === id))
    .filter((framework): framework is ValuationFrameworkResult => framework != null)
  const blendedRange = equityValueRange(methodFrameworks, weights)
  const blendedEnterpriseRange = inferredEnterpriseRangeFromEquityRange(blendedRange, valuation.base_inputs)
  return (
    <section className="valuation-detail-section" aria-label="Valuation methods and assumptions">
      <div className="section-title">
        <h2>Valuation methods</h2>
        <span className="meta">market-weighted reconciliation</span>
      </div>
      <p className="valuation-detail-intro">
        Enterprise value estimates the operating business before cash and debt. Equity value is what owners keep after
        subtracting debt and adding cash. The headline estimate weights each method by industry fit and data reliability.
      </p>
      <div className="valuation-method-table-wrap">
        <table className="valuation-method-table">
          <thead>
            <tr>
              <th scope="col">Method</th>
              <th scope="col">Weight</th>
              <th scope="col">Enterprise value</th>
              <th scope="col">Equity value</th>
            </tr>
          </thead>
          <tbody>
            {methodFrameworks.map((framework) => {
              const applicable = (framework.equity_value_mid ?? framework.equity_value) != null
              const enterpriseRange = frameworkEnterpriseValueRange(framework, valuation.base_inputs)
              return (
                <tr key={framework.id} className={applicable ? undefined : 'valuation-method-unused'}>
                  <th scope="row">
                    <span>
                      {framework.label}
                      {applicable ? null : <span className="valuation-method-badge">Not used</span>}
                    </span>
                    <small>{framework.method_summary}</small>
                    {applicable
                      ? (framework.reconciliation_note ? <em>{framework.reconciliation_note}</em> : null)
                      : (
                        <em>
                          This method is not used because {methodUnavailableReason(framework.id)}. Adjust the assumptions
                          above to include it.
                        </em>
                      )}
                  </th>
                  <td>{applicable ? formatWeight(normalizedWeightShare(weights, framework.id)) : '—'}</td>
                  <td>
                    {applicable ? (
                      <>
                        <span>{formatMethodRange(enterpriseRange.low, enterpriseRange.mid, enterpriseRange.high)}</span>
                        {enterpriseRange.inferred ? (
                          <small title="Inferred from equity value plus debt, less cash.">inferred</small>
                        ) : null}
                      </>
                    ) : (
                      <span className="valuation-method-na">Not used</span>
                    )}
                  </td>
                  <td>
                    {applicable
                      ? formatMethodRange(framework.equity_value_low, framework.equity_value_mid ?? framework.equity_value, framework.equity_value_high)
                      : <span className="valuation-method-na">Not used</span>}
                  </td>
                </tr>
              )
            })}
            {blendedRange.mid != null ? (
              <tr className="valuation-method-blended">
                <th scope="row">
                  <span>Blended estimate</span>
                  <small>Weighted reconciliation of the available methods using the active method weights.</small>
                  <em>Market comps carry most of the default estimate; DCF remains a visible cross-check.</em>
                </th>
                <td>100%</td>
                <td>
                  <span>{formatMethodRange(blendedEnterpriseRange.low, blendedEnterpriseRange.mid, blendedEnterpriseRange.high)}</span>
                  {blendedEnterpriseRange.inferred ? (
                    <small title="Inferred from blended equity value plus debt, less cash.">inferred</small>
                  ) : null}
                </td>
                <td>{formatMethodRange(blendedRange.low, blendedRange.mid, blendedRange.high)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <details className="valuation-weight-disclosure">
        <summary>
          <span>Method weights</span>
          <b>{weightsChanged ? 'custom' : 'default'}</b>
        </summary>
        <ValuationWeightControls
          weights={weights}
          defaultWeights={defaultWeights}
          weightsChanged={weightsChanged}
          onWeightChange={onWeightChange}
          onResetWeights={onResetWeights}
        />
      </details>

      <div className="section-title valuation-assumption-title">
        <h2>Inputs &amp; assumptions</h2>
        <span className="meta">base case, as of {formatAsOf(valuation.as_of)}</span>
      </div>
      <div className="valuation-input-grid">
        {inputTiles.map((tile) => (
          <button
            type="button"
            className={`valuation-input-tile${tile.detail ? ' clickable' : ''}${selectedInput?.id === tile.id ? ' selected' : ''}`}
            key={tile.id}
            disabled={!tile.detail}
            aria-pressed={tile.detail ? selectedInput?.id === tile.id : undefined}
            onPointerDown={(event) => {
              if (!tile.detail) return
              event.preventDefault()
              toggleInput(tile.id)
            }}
            onKeyDown={(event) => {
              if (!tile.detail || (event.key !== 'Enter' && event.key !== ' ')) return
              event.preventDefault()
              toggleInput(tile.id)
            }}
          >
            <span>{tile.label}</span>
            <b>{tile.value}</b>
            {tile.note ? <small>{tile.note}</small> : null}
          </button>
        ))}
      </div>
      {selectedInput ? <ValuationInputDetailCard tile={selectedInput} /> : null}
      {valuation.assumptions.notes.length ? (
        <ul className="valuation-assumption-notes">
          {valuation.assumptions.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

function valuationInputTiles(valuation: ValuationOverviewResponse): ValuationInputTile[] {
  const base = valuation.base_inputs
  const assumptions = valuation.assumptions
  const detail = (
    kind: ValuationInputDetailKind,
    formula: string,
    description: string,
    selector: (point: ValuationHistoryPoint) => number | null | undefined,
  ): ValuationInputDetail | undefined => {
    const series = valuationInputSeries(valuation, selector)
    return series.length >= 2 ? { kind, formula, description, series } : undefined
  }
  return [
    {
      id: 'revenue_ttm',
      label: 'Revenue (TTM)',
      value: formatCurrencyCompact(base.revenue_ttm),
      detail: detail('currency', 'Trailing 12-month revenue', 'Revenue scale is the base for revenue-multiple and margin-sensitive methods.', (point) => point.revenue_ttm),
    },
    {
      id: 'ebitda_ttm',
      label: 'EBITDA (TTM)',
      value: formatCurrencyCompact(base.ebitda_ttm),
      detail: detail('currency', 'Trailing 12-month EBITDA', 'EBITDA is the core operating-profit input for the EBITDA multiple and DCF methods.', (point) => point.ebitda_ttm),
    },
    {
      id: 'net_income_ttm',
      label: 'Net income (TTM)',
      value: formatCurrencyCompact(base.net_income_ttm),
      note: 'owner earnings proxy until add-backs are modeled',
      detail: detail('currency', 'Trailing 12-month net income', 'Net income currently stands in for owner earnings until add-backs are modeled.', (point) => point.net_income_ttm),
    },
    {
      id: 'revenue_growth',
      label: 'Revenue growth',
      value: formatPercentPoint(base.revenue_growth, 1),
      detail: revenueGrowthDetail(valuation),
    },
    {
      id: 'ebitda_margin',
      label: 'EBITDA margin',
      value: formatPercentPoint(base.ebitda_margin, 1),
      detail: detail('percent', 'EBITDA divided by revenue', 'EBITDA margin shows how much revenue becomes operating profit before financing and taxes.', (point) => point.ebitda_margin),
    },
    {
      id: 'net_margin',
      label: 'Net margin',
      value: formatPercentPoint(base.net_margin, 1),
      detail: detail('percent', 'Net income divided by revenue', 'Net margin is the owner-earnings proxy behind the small-business multiple method.', (point) => point.net_margin),
    },
    {
      id: 'total_debt',
      label: base.debt_basis === 'interest_bearing' ? 'Total debt (loans & cc)' : 'Total debt (liab.)',
      value: formatCurrencyCompact(base.total_debt),
      note: 'used in the equity bridge',
      detail: detail(
        'currency',
        base.debt_basis === 'interest_bearing' ? 'Point-in-time interest-bearing debt' : 'Point-in-time debt and liabilities',
        base.debt_basis === 'interest_bearing'
          ? 'Loans, notes, and credit cards reduce equity value after enterprise value is estimated; A/P and accruals are excluded.'
          : 'Debt reduces equity value after enterprise value is estimated.',
        (point) => point.total_debt,
      ),
    },
    {
      id: 'cash',
      label: 'Cash',
      value: formatCurrencyCompact(base.cash),
      note: 'added back to equity value',
      detail: detail('currency', 'Point-in-time cash', 'Cash is added back when enterprise value is bridged to equity value.', (point) => point.cash),
    },
    { id: 'earnings_multiple', label: 'Owner earnings multiple', value: `${assumptions.earnings_multiple.toFixed(2)}x` },
    { id: 'ebitda_multiple', label: 'EBITDA multiple', value: `${assumptions.ebitda_multiple.toFixed(1)}x` },
    { id: 'revenue_multiple', label: 'Revenue multiple', value: `${assumptions.revenue_multiple.toFixed(2)}x` },
    { id: 'wacc', label: 'Cost of capital', value: formatPercentPoint(assumptions.wacc, 1) },
    { id: 'forecast_growth', label: 'Forecast growth', value: formatPercentPoint(assumptions.forecast_growth, 1) },
    { id: 'terminal_growth', label: 'Terminal growth', value: formatPercentPoint(assumptions.terminal_growth, 2) },
  ]
}

function valuationInputSeries(
  valuation: ValuationOverviewResponse,
  selector: (point: ValuationHistoryPoint) => number | null | undefined,
): Array<{ period: string; label: string; value: number }> {
  return (valuation.history?.points || [])
    .map((point) => {
      const value = selector(point)
      return isFiniteNumber(value)
        ? { period: point.period, label: formatMonthLabel(point.period), value }
        : null
    })
    .filter((point): point is { period: string; label: string; value: number } => point != null)
}

function revenueGrowthDetail(valuation: ValuationOverviewResponse): ValuationInputDetail | undefined {
  const direct = valuationInputSeries(valuation, (point) => point.revenue_growth)
  const series = direct.length >= 2 ? direct : derivedRevenueGrowthSeries(valuation)
  return series.length >= 2
    ? {
        kind: 'percent',
        formula: direct.length >= 2 ? 'TTM revenue growth rate' : 'TTM revenue change from the prior modeled month',
        description: 'Growth changes the revenue story and future cash-flow assumptions.',
        series,
      }
    : undefined
}

function derivedRevenueGrowthSeries(valuation: ValuationOverviewResponse): Array<{ period: string; label: string; value: number }> {
  const revenuePoints = valuationInputSeries(valuation, (point) => point.revenue_ttm)
  const out: Array<{ period: string; label: string; value: number }> = []
  for (let index = 1; index < revenuePoints.length; index += 1) {
    const prior = revenuePoints[index - 1]
    const current = revenuePoints[index]
    if (!prior || !current || prior.value <= 0) continue
    out.push({
      period: current.period,
      label: current.label,
      value: current.value / prior.value - 1,
    })
  }
  return out
}

function ValuationInputDetailCard({ tile }: { tile: ValuationInputTile }) {
  const detail = tile.detail
  if (!detail || detail.series.length < 2) return null
  const first = detail.series[0]
  const last = detail.series[detail.series.length - 1]
  const change = last.value - first.value
  const favorable = change > 0
  const kindLabel = detail.kind === 'percent' || detail.kind === 'multiple' ? 'Ratio detail' : 'Input detail'
  return (
    <article className="valuation-input-detail card rise" aria-label={`${tile.label} detail`}>
      <div className="valuation-input-detail-head">
        <div>
          <div className="valuation-input-detail-title">
            <h3>{tile.label}</h3>
            <span>{kindLabel}</span>
          </div>
          <p>{detail.description}</p>
          <div className="valuation-input-detail-formula">{detail.formula}</div>
        </div>
        <div className="valuation-input-detail-stats">
          <ValuationInputStat label={`Beginning ${first.label}`} value={formatValuationInputValue(detail.kind, first.value)} />
          <ValuationInputStat label={`Ending ${last.label}`} value={formatValuationInputValue(detail.kind, last.value)} accent />
          <ValuationInputStat
            label="Change"
            value={formatValuationInputChange(detail.kind, change)}
            tone={change === 0 ? 'flat' : favorable ? 'up' : 'down'}
          />
        </div>
      </div>
      <TrendLine
        className="fig-svg"
        ariaLabel={`${tile.label} beginning to ending trend`}
        points={detail.series.map((point) => ({ label: point.label, value: point.value }))}
        axisLabels={[first.label, last.label]}
        formatValue={(value) => formatValuationInputValue(detail.kind, value)}
        width={920}
        height={220}
        interactive
        showStartValue
      />
    </article>
  )
}

function ValuationInputStat({
  label,
  value,
  accent = false,
  tone = 'flat',
}: {
  label: string
  value: string
  accent?: boolean
  tone?: 'up' | 'down' | 'flat'
}) {
  return (
    <div className={`valuation-input-stat ${accent ? 'accent' : ''} ${tone}`}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  )
}

function formatValuationInputValue(kind: ValuationInputDetailKind, value: number): string {
  if (kind === 'currency') return formatCurrencyCompact(value)
  if (kind === 'multiple') return `${value.toFixed(2)}x`
  return formatPercentPoint(value, 1)
}

function formatValuationInputChange(kind: ValuationInputDetailKind, value: number): string {
  if (!isFiniteNumber(value) || Math.abs(value) < 0.00001) return 'flat'
  if (kind === 'currency') return formatSignedCurrency(value)
  if (kind === 'multiple') return formatSignedMultipleDelta(value)
  return formatSignedPercentPointDelta(value)
}

function formatWeight(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return 'n/a'
  return `${Math.round(value * 100)}%`
}

function formatMethodRange(low: number | null | undefined, mid: number | null | undefined, high: number | null | undefined): string {
  if (isFiniteNumber(low) && isFiniteNumber(high) && Math.abs(high - low) > 1) {
    return `${formatCurrencyCompact(low)}-${formatCurrencyCompact(high)}`
  }
  if (isFiniteNumber(mid)) return formatCurrencyCompact(mid)
  return '-'
}

type ValuationSliderProps = {
  label: string
  description?: string
  impact?: string
  value: number
  baseValue: number
  min: number
  max: number
  step: number
  disabled?: boolean
  disabledText?: string
  inverse?: boolean
  formatValue: (value: number) => string
  formatDelta: (value: number) => string
  onChange: (value: number) => void
}

function ValuationSlider({
  label,
  description,
  impact,
  value,
  baseValue,
  min,
  max,
  step,
  disabled = false,
  disabledText = 'Unavailable',
  inverse = false,
  formatValue,
  formatDelta,
  onChange,
}: ValuationSliderProps) {
  const delta = value - baseValue
  const favorable = inverse ? delta < 0 : delta > 0
  const unfavorable = inverse ? delta > 0 : delta < 0
  const basePosition = max > min ? Math.min(100, Math.max(0, ((baseValue - min) / (max - min)) * 100)) : 50
  const valuePosition = max > min ? Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100)) : 50
  const handleInput = (event: SyntheticEvent<HTMLInputElement>) => onChange(Number(event.currentTarget.value))
  return (
    <label className="valuation-slider" aria-disabled={disabled}>
      <span className="valuation-slider-head">
        <span>{label}</span>
        <b>{disabled ? disabledText : formatValue(value)}</b>
      </span>
      {description ? (
        <span className="valuation-slider-description">
          <span>{description}</span>
          {impact ? <b>{impact}</b> : null}
        </span>
      ) : null}
      <span
        className="valuation-slider-track"
        style={{
          '--start-position': `${basePosition}%`,
          '--start-ratio': `${basePosition / 100}`,
          '--value-position': `${valuePosition}%`,
          '--value-ratio': `${valuePosition / 100}`,
        } as CSSProperties}
      >
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          aria-label={label}
          onInput={handleInput}
          onChange={handleInput}
          onMouseUp={handleInput}
          onPointerUp={handleInput}
          onKeyUp={handleInput}
        />
        <span className="valuation-slider-start" aria-hidden="true">
          <span className="valuation-slider-start-tick" />
        </span>
      </span>
      <span className="valuation-slider-foot">
        <span>{disabled ? 'Unavailable' : 'Change from base'}</span>
        <span className={favorable ? 'good' : unfavorable ? 'bad' : undefined}>{disabled ? '—' : formatDelta(delta)}</span>
      </span>
    </label>
  )
}

export default CfoValuationPage
